import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Divider, Stack, Text } from "@mantine/core";
import _ from "lodash";
import router from "next/router";

import { ActiveWithinSelector } from "components/ActiveWithinSelector";
import { CompanyStageSelector } from "components/CompanyStageSelector";
import { LocationSelector } from "components/LocationSelector";
import { SectionToggle } from "components/SectionToggle";
import { YOE_OPTIONS_ARR, INDUSTRY_OPTIONS } from "constants/explorer_selection_options";
import { useSearchRouteInfo } from "hooks/routing";
import { CompanyNameOptionValue } from "lib/frontend/benchmarking/types";
import { dictionaryToSearchParams } from "utils/utils";

import { CollapsibleFilter, FiltersSelectedBadge } from "./CollapsibleFilter";

export function FilterSidebar(): JSX.Element {
  const { baseRoute, query, queryString, focusedJobTitle, searchMode } = useSearchRouteInfo();
  const nextRouteData = useRef(_.clone(query));

  useEffect(() => {
    nextRouteData.current = _.clone(query);
  }, [queryString, query]);

  const [companyNames, setCompanyNames] = useState<CompanyNameOptionValue[]>([
    ...query.companyName,
  ]);

  function addToCompanyNames(newNames: string[]) {
    setCompanyNames((oldNames) => {
      const resultingNames = _.uniq([...oldNames, ...newNames]).sort();

      return resultingNames;
    });
  }

  async function fetchCompanyNames(page = 0) {
    return fetch(
      `/benchmarking/api/companies-autocomplete/_?page=${page}&title=${focusedJobTitle || ""}`,
    )
      .then((res) => res.json())
      .then((data) => {
        const formattedCompanyNames = _.map(data, (companyName) => companyName.name);
        addToCompanyNames(formattedCompanyNames);
      });
  }

  useEffect(() => {
    fetchCompanyNames(0);
  }, []);

  const handleChange = useCallback((name: string, v: string[] | [number, number] | null) => {
    const nextQuery = nextRouteData.current;
    if (v) {
      _.set(nextQuery, name, v);
    } else if (name === "capitalRaised") {
      _.set(nextQuery, "capitalRaised", null);
    } else {
      _.set(nextQuery, name, []);
    }

    _.set(nextQuery, "offset", 0);

    const nextQueryString = dictionaryToSearchParams(nextQuery);
    router.push(`${baseRoute}/${nextQueryString}`, undefined, { scroll: false });
  }, []);

  const filterByLocationSection = useMemo(() => {
    return query.tier.length > 0 || query.location.length > 0;
  }, [query.tier, query.location]);

  const filterByStageSection = useMemo(() => {
    return query.headcount.length > 0 || query.stage.length > 0 || query.capitalRaised !== null;
  }, [query.headcount, query.stage, query.capitalRaised]);

  return (
    <Stack>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        Filters
      </Text>

      <SectionToggle
        initiallyOpen={filterByLocationSection}
        title="Location"
        // eslint-disable-next-line react/jsx-no-undef
        rightSection={
          filterByLocationSection && (
            <FiltersSelectedBadge>{`${
              query.tier.length + query.location.length
            } Selected`}</FiltersSelectedBadge>
          )
        }
      >
        <LocationSelector mt={8} />
      </SectionToggle>
      <Divider />
      <SectionToggle
        initiallyOpen={filterByStageSection}
        title="Company Stage"
        rightSection={
          filterByStageSection && (
            <FiltersSelectedBadge>{`${
              query.headcount.length + query.stage.length + (query.capitalRaised !== null ? 1 : 0)
            } Selected`}</FiltersSelectedBadge>
          )
        }
      >
        <CompanyStageSelector mt={8} />
      </SectionToggle>
      <Divider />
      {/* <CollapsibleFilter
        label="Funding Stage"
        data={STAGE_OPTIONS}
        queryParamName="stage"
        onChange={(v) => { handleChange("stage", v); }}
      />
      <Divider /> */}
      <SectionToggle title="Age of Data">
        <ActiveWithinSelector mt={8} />
      </SectionToggle>
      {/* <Divider /> */}
      {/* <SidebarCapRaisedSection
        handleChange={handleChange}
        queryParamName="capitalRaised"
      /> */}
      <Divider />
      {searchMode !== "family & level" && (
        <>
          <CollapsibleFilter
            label="Experience"
            selectionSubtext="minimum years of experience"
            data={YOE_OPTIONS_ARR}
            queryParamName="yoe"
            onChange={(v) => {
              handleChange("yoe", v);
            }}
          />
          <Divider />
        </>
      )}
      {/* <CollapsibleFilter
        label="Headcount"
        data={HEADCOUNT_OPTIONS}
        queryParamName="headcount"
        onChange={(v) => { handleChange("headcount", v); }}
      />
      <Divider /> */}
      <CollapsibleFilter
        label="Industry"
        data={INDUSTRY_OPTIONS}
        queryParamName="industry"
        onChange={(v) => {
          handleChange("industry", v);
        }}
      />
      <Divider />
      <CollapsibleFilter
        label="Company"
        queryParamName="companyName"
        data={companyNames}
        onChange={(v) => {
          handleChange("companyName", v);
        }}
        maxNumberListItemsToRender={Number.MAX_SAFE_INTEGER}
        showSearch
        onSearchChange={(v) => {
          if (_.isEmpty(v)) {
            return;
          }

          fetch(`/api/benchmarking/companies-autocomplete/${v}`)
            .then((res) => res.json())
            .then((data) => {
              const formattedCompanyNames = _.map(
                data,
                (companyName) => companyName.name as string,
              );
              addToCompanyNames(formattedCompanyNames);
            });
        }}
        fetchPage={(page) => fetchCompanyNames(page)}
      />
    </Stack>
  );
}
